import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Divider,
  RadioGroup,
  Radio,
  Icon,
  IconButton,
  Spinner,
  HStack,
  Stack,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FaApple, FaBitcoin, FaCreditCard } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import { useSubscription } from '../../../context/SubscriptionContext';

const PaymentMethodSelect = ({ selectedPaymentMethod, setSelectedPaymentMethod }) => (
  <FormControl w="100%" mt={4} bg="secondary.900" p={3} borderRadius="md">
    <FormLabel fontSize="lg" fontWeight="bold" color="primary.500">
      Payment Method
    </FormLabel>
    <RadioGroup onChange={setSelectedPaymentMethod} value={selectedPaymentMethod}>
      <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
        <Box display="flex" alignItems="center" cursor="pointer">
          <Radio value="card" colorScheme="blue">
            <HStack>
              <Icon as={FaCreditCard} boxSize={5} />
              <Text color="primary.500">Card</Text>
            </HStack>
          </Radio>
        </Box>
        <Box display="flex" alignItems="center" cursor="pointer">
          <Radio value="applePay" colorScheme="blue" isDisabled>
            <HStack>
              <Icon as={FaApple} boxSize={5} />
              <Text color="primary.500">Apple Pay</Text>
            </HStack>
          </Radio>
        </Box>
        <Box display="flex" alignItems="center" cursor="pointer">
          <Radio value="cryptoPay" colorScheme="blue" isDisabled>
            <HStack>
              <Icon as={FaBitcoin} boxSize={5} />
              <Text color="primary.500">Crypto Pay</Text>
            </HStack>
          </Radio>
        </Box>
      </Stack>
    </RadioGroup>
  </FormControl>
);

const BillingDetailsForm = ({ register, errors }) => (
  <>
    <FormControl mt={4} isInvalid={errors.name}>
      <FormLabel fontSize="md" color="primary.500">Name</FormLabel>
      <Input
        type="text"
        {...register('name', { required: 'Name is required' })}
        variant="filled"
        size="md"
        w="full"
      />
    </FormControl>
    <FormControl mt={4} isInvalid={errors.address}>
      <FormLabel fontSize="md" color="primary.500">Address</FormLabel>
      <Input
        type="text"
        {...register('address', { required: 'Address is required' })}
        variant="filled"
        size="md"
        w="full"
      />
    </FormControl>
    <Flex mt={4} gap={4}>
      <FormControl isInvalid={errors.city}>
        <FormLabel fontSize="md" color="primary.500">City</FormLabel>
        <Input
          type="text"
          {...register('city', { required: 'City is required' })}
          variant="filled"
          size="md"
          w="full"
        />
      </FormControl>
      <FormControl isInvalid={errors.state}>
        <FormLabel fontSize="md" color="primary.500">State</FormLabel>
        <Input
          type="text"
          {...register('state', { required: 'State is required' })}
          variant="filled"
          size="md"
          w="full"
        />
      </FormControl>
    </Flex>
    <FormControl mt={4} isInvalid={errors.zip}>
      <FormLabel fontSize="md" color="primary.500">ZIP Code</FormLabel>
      <Input
        type="text"
        {...register('zip', { required: 'ZIP Code is required' })}
        variant="filled"
        size="md"
        w="full"
      />
    </FormControl>
  </>
);

const Settings = () => {
  const {
    paymentMethods,
    getPaymentMethods,
    setDefaultPaymentMethod,
    createPaymentMethod,
    addPaymentMethod,
    deletePaymentMethod,
    loading,
  } = useSubscription();

  const stripe = useStripe();
  const elements = useElements();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [isAdding, setIsAdding] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);

  // Track which method is being set default or deleted
  const [deletingMethodId, setDeletingMethodId] = useState(null);
  const [settingDefaultMethodId, setSettingDefaultMethodId] = useState(null);

  useEffect(() => {
    getPaymentMethods();
  }, [getPaymentMethods]);

  // Add new Payment Method
  const handleAddPaymentMethod = async (data) => {
    setIsPaymentLoading(true);
    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card Element not found.');

      const paymentMethodId = await createPaymentMethod(cardElement, stripe, data);
      await addPaymentMethod(paymentMethodId);
      setIsAdding(false);
    } catch (error) {
      console.error('Error adding payment method:', error);
    } finally {
      setIsPaymentLoading(false);
    }
  };

  // Set default Payment Method
  const handleSetDefaultPaymentMethod = async (id) => {
    setSettingDefaultMethodId(id);
    try {
      await setDefaultPaymentMethod(id);
    } catch (error) {
      console.error('Error setting default payment method:', error);
    } finally {
      setSettingDefaultMethodId(null);
    }
  };



  const CARD_OPTIONS = {
    style: {
      base: {
        fontSize: "16px",
        color: "#424770", // ✅ Your requested text color
        fontFamily: "Poppins, sans-serif",
        "::placeholder": { color: "#aab7c4" }, // ✅ Your requested placeholder color
        padding: "12px",
        backgroundColor: "transparent",
        borderRadius: "6px",
      },
      invalid: {
        color: "#ff4c4c", // Error text color
        iconColor: "#ff4c4c",
      },
      complete: {
        color: "#4caf50", // Success color
      },
    },
    hidePostalCode: true, // Hide postal code if not needed
    disableLink:true,
  };

  // Delete Payment Method
  const handleDeletePaymentMethod = async (id) => {
    setDeletingMethodId(id);
    try {
      await deletePaymentMethod(id);
    } catch (error) {
      console.error('Error deleting payment method:', error);
    } finally {
      setDeletingMethodId(null);
    }

  };

  return (
    <Box p={3}>
      <Heading mb={6} color="primary.900">
        Settings
      </Heading>

      <Box mb={8}>
        <Heading size="md" mb={4} color="primary.500">
          Payment Methods
        </Heading>
        {loading ? (
          <Spinner size="lg" color="primary.500" />
        ) : paymentMethods.length === 0 ? (
          <Text color="primary.500">No payment methods available.</Text>
        ) : (
          paymentMethods.map((method) => (
            <Flex
              key={method.id}
              align={{ base: 'flex-start', md: 'center' }}
              flexDirection={{ base: 'column', md: 'row' }}
              justify="space-between"
              p={4}
              borderWidth="1px"
              borderRadius="md"
              bg="secondary.500"
              mb={4}
            >
              <Box>
                <Text color="primary.900">
                  **** **** **** {method.card.last4} ({method.card.brand})
                </Text>
                <Text color="primary.500">
                  Expires: {method.card.exp_month}/{method.card.exp_year}
                </Text>
              </Box>

              <Flex my={5}>
                <Button
                  mr={2}
                  size="sm"
                  onClick={() => handleSetDefaultPaymentMethod(method.id)}
                  isDisabled={method.is_default}
                  isLoading={settingDefaultMethodId === method.id}
                >
                  {method.is_default ? 'Default' : 'Set Default'}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleDeletePaymentMethod(method.id)}
                  isLoading={deletingMethodId === method.id}
                  colorScheme="red"
                >
                  Delete
                </Button>
              </Flex>
            </Flex>
          ))
        )}
      </Box>

      <Box mb={8}>
        {isAdding ? (
          <Box as="form" onSubmit={handleSubmit(handleAddPaymentMethod)}>
            <Heading size="md" mb={4} color="primary.500">
              Add Payment Method
            </Heading>
            <PaymentMethodSelect
              selectedPaymentMethod={selectedPaymentMethod}
              setSelectedPaymentMethod={setSelectedPaymentMethod}
            />
            {selectedPaymentMethod === 'card' && (
              <>
                <BillingDetailsForm register={register} errors={errors} />

                {/* Card details */}
                <FormControl mt={4}>
                  <FormLabel color="primary.500">Card Details</FormLabel>
                                            <Box
                                                p={4}
                                                borderWidth="1px"
                                                borderRadius="md"
                                                bg="white"
                                                _focus={{ borderColor: "primary.500", boxShadow: "0px 0px 8px rgba(72, 187, 120, 0.8)" }}
                                            >
                                                <CardElement options={CARD_OPTIONS} />
                                            </Box>
                </FormControl>

                <Flex mt={4} gap={4}>
                  <Button type="submit" isLoading={isPaymentLoading} colorScheme="teal">
                    Save
                  </Button>
                  <Button onClick={() => setIsAdding(false)} colorScheme="red">
                    Cancel
                  </Button>
                </Flex>
              </>
            )}
          </Box>
        ) : (
          <Flex align="center" gap={5}>
            <Text color="primary.100">Add</Text>
            <IconButton
              aria-label="Add Payment Method"
              icon={<AddIcon />}
              size="sm"
              onClick={() => setIsAdding(true)}
            />
          </Flex>
        )}
      </Box>

      <Divider />
    </Box>
  );
};

export default Settings;